import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';

const AdminSales = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending'); // pending | approved | manual | cancelled | all
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/credits/orders/all');
      // FIX: NÃO filtrar status — admin precisa ver pendentes para aprovar.
      setOrders(response.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Counts por aba
  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, manual: 0, manual_debit: 0, cancelled: 0, all: orders.length };
    orders.forEach(o => { if (c[o.status] !== undefined) c[o.status]++; });
    return c;
  }, [orders]);

  // Filtragem por aba + busca
  const filteredOrders = useMemo(() => {
    let list = orders;
    if (tab !== 'all') list = list.filter(o => o.status === tab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(o =>
        String(o.id).includes(q) ||
        (o.username || '').toLowerCase().includes(q) ||
        String(o.quantity).includes(q)
      );
    }
    return list;
  }, [orders, tab, search]);

  const formatDateTime = (s) => {
    if (!s) return '—';
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const approveOrder = async (orderId) => {
    if (!window.confirm('Deseja aprovar este pedido e adicionar os créditos ao usuário?')) {
      return;
    }

    try {
      await api.put(`/credits/orders/${orderId}/approve`);
      await fetchOrders();
    } catch (error) {
      console.error('Error approving order:', error);
      alert('Erro ao aprovar pedido');
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Deseja cancelar este pedido?')) {
      return;
    }

    try {
      await api.put(`/credits/orders/${orderId}/cancel`);
      await fetchOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert('Erro ao cancelar pedido');
    }
  };

  if (loading) {
    return (
      <>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
        <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
        <style>{`
          .as-loading { min-height: 100vh; background: #f5f5f5; display: flex; align-items: center; justify-content: center; font-family: 'Lexend', sans-serif; color: #666; flex-direction: column; gap: 12px; }
          .as-loading-spinner { width: 36px; height: 36px; border: 3px solid rgba(255,153,0,0.2); border-top-color: #f90; border-radius: 50%; animation: as-spin 0.8s linear infinite; }
          @keyframes as-spin { to { transform: rotate(360deg); } }
        `}</style>
        <div className="as-loading">
          <div className="as-loading-spinner"></div>
          <p>Carregando vendas...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .as *, .as *::before, .as *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .as { font-family: 'Lexend', sans-serif; color: #555; background: #f5f5f5; min-height: 100vh; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
        
        /* ===== HEADER ===== */
        .as-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 18px 30px; display: flex; align-items: center; justify-content: space-between;
          box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 1000;
        }
        .as-header-left { display: flex; align-items: center; gap: 20px; }
        .as-header-actions { display: flex; align-items: center; gap: 12px; }
        .as-header-logo { width: 40px; height: 40px; flex-shrink: 0; }
        .as-header-logo img { width: 100%; height: 100%; object-fit: contain; }
        .as-header-title { font-size: 22px; font-weight: 700; color: #fff; }
        .as-header-back {
          display: flex; align-items: center; gap: 8px; color: #f90;
          cursor: pointer; transition: all 0.3s; font-weight: 500;
        }
        .as-header-back:hover { color: #fff; transform: translateX(-3px); }
        .as-header-refresh {
          display: flex; align-items: center; gap: 8px; color: #f90;
          cursor: pointer; transition: all 0.3s; font-weight: 500;
        }
        .as-header-refresh:hover { color: #fff; }

        /* ===== MAIN ===== */
        .as-main { max-width: 1200px; margin: 0 auto; padding: 35px 20px; }
        .as-card {
          background: #fff; border-radius: 20px; box-shadow: 0 4px 20px rgba(0,0,0,0.06);
          border: 1px solid rgba(0,0,0,0.04); overflow: hidden;
        }
        .as-table { width: 100%; border-collapse: collapse; }
        .as-table th {
          background: #f8f9fa; padding: 16px 20px; text-align: left;
          font-size: 13px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.5px;
        }
        .as-table td { padding: 16px 20px; border-bottom: 1px solid #f0f0f0; }
        .as-table tr:hover { background: #f8f9fa; }
        .as-table tr:last-child td { border-bottom: none; }
        .as-badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .as-badge.approved { background: rgba(56,161,105,0.15); color: #38a169; }
        .as-badge.pending { background: rgba(237,137,54,0.18); color: #d97706; animation: as-blink 2s ease-in-out infinite; }
        .as-badge.cancelled { background: rgba(229,62,62,0.15); color: #e53e3e; }
        .as-badge.manual { background: rgba(168,85,247,0.15); color: #a855f7; }
        .as-badge.manual_debit { background: rgba(220,38,38,0.15); color: #dc2626; }
        @keyframes as-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.65; } }

        /* TABS */
        .as-tabs { display: flex; gap: 6px; padding: 16px 20px 0; border-bottom: 1px solid #eee; flex-wrap: wrap; }
        .as-tab {
          padding: 10px 16px; border: none; background: transparent; cursor: pointer;
          font-family: 'Lexend', sans-serif; font-size: 13px; font-weight: 600; color: #888;
          border-bottom: 3px solid transparent; transition: all 0.2s;
          display: inline-flex; align-items: center; gap: 8px;
        }
        .as-tab:hover { color: #f90; }
        .as-tab.active { color: #f90; border-bottom-color: #f90; }
        .as-tab-count {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 22px; height: 22px; padding: 0 7px;
          border-radius: 11px; font-size: 11px; font-weight: 700;
          background: #f0f0f0; color: #666;
        }
        .as-tab.active .as-tab-count { background: #f90; color: #fff; }
        .as-tab.warning .as-tab-count { background: #fb923c; color: #fff; }

        .as-toolbar { padding: 16px 20px; display: flex; gap: 12px; align-items: center; border-bottom: 1px solid #eee; flex-wrap: wrap; }
        .as-search {
          flex: 1; min-width: 220px; padding: 10px 14px 10px 38px;
          border: 1px solid #e0e0e0; border-radius: 10px;
          font-family: 'Lexend', sans-serif; font-size: 13px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23999'%3E%3Cpath d='M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: 12px center; background-size: 16px;
          outline: none; transition: all 0.2s;
        }
        .as-search:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15); }
        .as-toolbar-info { font-size: 13px; color: #888; }
        .as-actions { display: flex; gap: 10px; }
        .as-btn {
          display: flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 10px;
          font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.3s; border: none;
        }
        .as-btn.approve { background: #38a169; color: #fff; }
        .as-btn.approve:hover { background: #2f855a; }
        .as-btn.cancel { background: #e53e3e; color: #fff; }
        .as-btn.cancel:hover { background: #c53030; }
        .as-text-approved { color: #38a169; font-weight: 600; }
        .as-text-cancelled { color: #e53e3e; font-weight: 600; }

        .as-empty {
          text-align: center; padding: 60px 20px;
        }
        .as-empty-icon {
          width: 80px; height: 80px; background: #f0f0f0; border-radius: 20px;
          display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;
        }
        .as-empty-icon i { font-size: 32px; color: #999; }
        .as-empty p { color: #999; font-size: 16px; }

        @media (max-width: 768px) {
          .as-header { padding: 15px 20px; }
          .as-header-title { font-size: 18px; }
          .as-header-left { gap: 15px; }
          .as-main { padding: 20px 15px; }
          .as-table th, .as-table td { padding: 12px 10px; font-size: 12px; }
          .as-actions { flex-direction: column; }
          .as-btn { padding: 10px 12px; justify-content: center; font-size: 12px; }
          .as-badge { font-size: 10px; padding: 4px 8px; }
        }

        @media (max-width: 480px) {
          .as-header { padding: 12px 15px; }
          .as-header-title { font-size: 16px; }
          .as-header-left { gap: 10px; }
          .as-header-logo { width: 35px; height: 35px; }
          .as-header-back, .as-header-refresh { font-size: 14px; }
          .as-main { padding: 15px 10px; }
          .as-table th, .as-table td { padding: 10px 8px; font-size: 11px; }
          .as-actions { flex-direction: column; gap: 5px; }
          .as-btn { padding: 8px 10px; justify-content: center; font-size: 11px; }
          .as-badge { font-size: 9px; padding: 3px 6px; }
          .as-empty { padding: 40px 15px; }
          .as-empty-icon { width: 60px; height: 60px; }
          .as-empty-icon i { font-size: 24px; }
          .as-empty p { font-size: 14px; }
        }
      `}</style>

      <div className="as">
        {/* Header */}
        <header className="as-header">
          <div className="as-header-left">
            <h1 className="as-header-title">Gerenciar Vendas</h1>
            {counts.pending > 0 && (
              <span className="as-badge pending" style={{ marginLeft: 12 }}>
                <i className="fas fa-bell"></i> {counts.pending} pendente{counts.pending !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="as-header-actions">
            <div className="as-header-refresh" onClick={fetchOrders}>
              <i className="fas fa-sync-alt"></i> Atualizar
            </div>
            {/* Botão "Voltar" removido — navegação já fica na sidebar (AdminLayout) */}
          </div>
        </header>

        {/* Main */}
        <main className="as-main">
          <div className="as-card">
            {/* Tabs */}
            <div className="as-tabs">
              {[
                { key: 'pending',   label: 'Pendentes',   icon: 'fa-clock',          warning: counts.pending > 0 },
                { key: 'approved',  label: 'Aprovadas',   icon: 'fa-check-circle' },
                { key: 'manual',    label: 'Adicionados', icon: 'fa-plus-circle' },
                { key: 'manual_debit', label: 'Removidos', icon: 'fa-minus-circle' },
                { key: 'cancelled', label: 'Canceladas',  icon: 'fa-ban' },
                { key: 'all',       label: 'Tudo',        icon: 'fa-list' },
              ].map(t => (
                <button
                  key={t.key}
                  className={`as-tab ${tab === t.key ? 'active' : ''} ${t.warning ? 'warning' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  <i className={`fas ${t.icon}`}></i>
                  {t.label}
                  <span className="as-tab-count">{counts[t.key] ?? 0}</span>
                </button>
              ))}
            </div>

            {/* Toolbar busca */}
            <div className="as-toolbar">
              <input
                type="text"
                className="as-search"
                placeholder="Buscar por #ID, usuário ou quantidade..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="as-toolbar-info">
                {filteredOrders.length} pedido{filteredOrders.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="as-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Usuário</th>
                    <th>Quantidade</th>
                    <th>Status</th>
                    <th>Data</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id}>
                    <td>#{order.id}</td>
                    <td>{order.username}</td>
                    <td style={{ fontWeight: 600 }}>{order.quantity} créditos</td>
                    <td>
                      <span className={`as-badge ${order.status}`}>
                        <i className={`fas ${
                          order.status === 'approved' ? 'fa-check-circle' :
                          order.status === 'cancelled' ? 'fa-ban' :
                          order.status === 'manual' ? 'fa-plus-circle' :
                          order.status === 'manual_debit' ? 'fa-minus-circle' :
                          'fa-clock'
                        }`}></i>
                        {order.status === 'approved' ? 'Aprovado' :
                         order.status === 'cancelled' ? 'Cancelado' :
                         order.status === 'manual' ? 'Adicionado pelo Admin' :
                         order.status === 'manual_debit' ? 'Removido pelo Admin' :
                         'Pendente'}
                      </span>
                    </td>
                    <td style={{ color: '#666', fontSize: 13 }}>{formatDateTime(order.created_at)}</td>
                    <td>
                      {order.status === 'pending' && (
                        <div className="as-actions">
                          <button onClick={() => approveOrder(order.id)} className="as-btn approve"><i className="fas fa-check"></i> Aprovar</button>
                          <button onClick={() => cancelOrder(order.id)} className="as-btn cancel"><i className="fas fa-times"></i> Cancelar</button>
                        </div>
                      )}
                      {order.status === 'approved' && <span className="as-text-approved"><i className="fas fa-check-circle"></i> Confirmado</span>}
                      {order.status === 'cancelled' && <span className="as-text-cancelled"><i className="fas fa-ban"></i> Cancelado</span>}
                      {order.status === 'manual' && <span style={{ color: '#a855f7', fontWeight: 600 }}><i className="fas fa-user-shield"></i> Admin</span>}
                      {order.status === 'manual_debit' && <span style={{ color: '#dc2626', fontWeight: 600 }}><i className="fas fa-user-shield"></i> Admin</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="as-empty">
              <div className="as-empty-icon"><i className="fas fa-shopping-cart"></i></div>
              <p>{search ? 'Nenhum pedido bate com a busca' : tab === 'pending' ? 'Nenhum pedido pendente — tudo em dia! 🎉' : 'Nenhum pedido nesta categoria'}</p>
            </div>
          )}
        </div>
        </main>
      </div>
    </>
  );
};

export default AdminSales;
